import { useEffect, useState, useContext, useRef, useMemo } from 'react';
import React from 'react'
import { useTranslation } from '../../../contexts/TranslationProvider'
import EditContractTemplateContext from '../../../contexts/EditContractTemplateContext';
import { ClauseEntity, SubClauseEntity } from "../../../domain/entities";
import { PropertyParam, SegmentedClauseParam } from '../../../domain/types/ClauseParams';
import CustomCombobox from '../../../components/common/CustomCombobox';
import CustomPropertiesContext from '../../../contexts/CustomPropertiesContext';

function EditPropertyParam({ param, clauseId, subClauseId }: { param: PropertyParam, clauseId: ClauseEntity['id'], subClauseId: SubClauseEntity['id'] }) {
  const { t, language } = useTranslation();
  const errorTranslationPath = t("pages.contractTemplateParam.addParamForm.errors");
  const translationPath = t("pages.contractTemplateParam.edition")
  const [errors, setErrors] = useState(null);
  const [paramName, setParamName] = useState(param?.name);
  const latestParamNameRef = useRef(paramName);
  const errorsRef = useRef(errors);

  const [isOpen, setIsOpen] = useState(false)
  const [isParentOpen, setIsParentOpen] = useState(false)
  const { tags, tagMapping } = useContext(CustomPropertiesContext)
  const updateName = () => {
    console.log("Updating param name:", latestParamNameRef.current);
    onParamNameChanged(param, latestParamNameRef.current);
  }

  useEffect(() => {
    return () => {
      if (!errorsRef.current) {
        updateName();
      } else {
        console.log("Errors found, not updating name.", errorsRef.current);
      }
    };
  }, []);

  const {
    onParamNameChanged,
    onParamChanged,
    contractTemplate,
    combinedTemplateParams
  } = useContext(EditContractTemplateContext);
  const translationTypes = "pages.contractTemplateParam.params"

  const onchange = (value) => {
    setErrors(null);
    errorsRef.current = null;
    setParamName(value);
    latestParamNameRef.current = value;
    if (!value) {
      setErrors(t(errorTranslationPath.nameMissing));
      errorsRef.current = t(errorTranslationPath.nameMissing);
    }
    if (!/^[a-z_][a-z0-9_]*$/i.test(value)) {
      setErrors(t(errorTranslationPath.nameWrong));
      errorsRef.current = t(errorTranslationPath.nameWrong);
    }
    const existingParam = combinedTemplateParams.find(param => param.name === value);
    if (!existingParam) {
      return;
    }
    if (existingParam && (param.name !== value)) {
      setErrors(t(errorTranslationPath.nameExists));
      errorsRef.current = t(errorTranslationPath.nameExists);
    }
  }
  const parentTagParams = useMemo(() => {
    if (param.args.tag && tagMapping[param.args.tag]) {
      const parentTag = tagMapping[param.args.tag]
      return combinedTemplateParams.filter(p => p.type == 'property' && p.args.tag == parentTag)
    }
    return []
  }, [combinedTemplateParams, param.args.tag])

  return (
    <div>
      {/* Edit param name */}
      <div className="edit-param-container">
        <div className="edit-param-name">
          <label className="label">{t(translationPath.parameterName)}</label>
          <input
            className="input"
            value={paramName}
            type="text"
            onChange={(e) => {
              onchange(e.target.value);
            }}
          />
        </div>
        <div className="error">
          {errors}
        </div>
      </div>
      {/* Edit parameter label */}
      <div className="edit-param-container">
        <div className="edit-param-name">
          <label className="label">{t(translationPath.parameterLabel)}</label>
          <input
            className="input"
            value={param?.label}
            type="text"
            onChange={(e) => {
              onParamChanged(clauseId, subClauseId, {
                ...param,
                label: e.target.value,
              })
            }}
            onBlur={(e) => {
              onParamChanged(clauseId, subClauseId, {
                ...param,
                label: e.target.value,
              })
            }}
          />
        </div>
      </div>
      <div className="edit-param-container">
        <div className="edit-param-name">
          <label className="label">{translationPath.parameterTag}</label>
          <CustomCombobox
            label={""}
            options={tags}
            fullWidth
            value={param?.args?.tag ?? ""}
            onChange={(value: string) => {
              param.args.tag = value
              onParamChanged(clauseId, subClauseId, param)
            }}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        </div>
      </div>
      {(parentTagParams.length > 0) &&

        <div className="edit-param-container">
          <div className="edit-param-name">
            <label className="label">{translationPath.parentTag}</label>
            <CustomCombobox
              label={""}
              options={parentTagParams.map(p => p.name)}
              value={param.args.parentParam}
              fullWidth
              onChange={(value: string) => {
                param.args.parentParam = value
                onParamChanged(clauseId, subClauseId, param)
              }}
              isOpen={isParentOpen}
              setIsOpen={setIsParentOpen}
            />
          </div>
        </div>
      }
    </div>
  )
}

export default EditPropertyParam